const API_BASE = 'https://nxdsjksrga.execute-api.eu-west-1.amazonaws.com';
const STATIC_API_TOKEN = import.meta.env.VITE_LUMINORA_API_TOKEN || null;
const DEFAULT_TEST_USER = 'democanvas';
const TEST_USER_ID = import.meta.env.VITE_LUMINORA_TEST_USER_ID || DEFAULT_TEST_USER;
const TEST_USERNAME = import.meta.env.VITE_LUMINORA_TEST_USERNAME || DEFAULT_TEST_USER;
const SUPPORTED_ARCHETYPES_SET = new Set(['sage', 'explorer', 'rebel', 'caregiver', 'everyman']);

let cachedDynamicToken: string | null = null;
let cachedTokenTimestamp: number | null = null;
let lastTokenFetchError: string | null = null;

const hasStaticToken = Boolean(STATIC_API_TOKEN);

const buildHeaders = async (forceRefresh = false): Promise<Record<string, string>> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = await getAuthToken(forceRefresh);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const getAuthToken = async (forceRefresh = false): Promise<string | null> => {
  if (hasStaticToken) {
    return STATIC_API_TOKEN;
  }

  if (!cachedDynamicToken || forceRefresh || isTokenExpired()) {
    cachedDynamicToken = await fetchTestingToken();
    cachedTokenTimestamp = cachedDynamicToken ? Date.now() : null;
  }

  return cachedDynamicToken;
};

const isTokenExpired = () => {
  if (!cachedTokenTimestamp) return true;
  const TWENTY_THREE_HOURS = 23 * 60 * 60 * 1000;
  return Date.now() - cachedTokenTimestamp > TWENTY_THREE_HOURS;
};

const fetchTestingToken = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${API_BASE}/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: TEST_USER_ID,
        username: TEST_USERNAME
      })
    });

    if (!response.ok) {
      const errorBody = await response.text();
      lastTokenFetchError = `No se pudo obtener token temporal (HTTP ${response.status}): ${errorBody || response.statusText}`;
      console.error(lastTokenFetchError);
      return null;
    }

    const data = await response.json();
    lastTokenFetchError = null;
    return data.token;
  } catch (error: any) {
    lastTokenFetchError = `Error de red al obtener token temporal: ${error?.message || error}`;
    console.error(lastTokenFetchError);
    return null;
  }
};

const executePost = async (
  endpoint: string,
  payload: Record<string, unknown>
): Promise<Response> => {
  validateOfficialPayload(payload);
  let headers = await buildHeaders();
  let response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });

  if (response.status === 401 && !hasStaticToken) {
    // Intentar refrescar token y reintentar una vez
    cachedDynamicToken = null;
    cachedTokenTimestamp = null;
    headers = await buildHeaders(true);
    if (headers.Authorization) {
      response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
    }
  }

  return response;
};

export const getLastTokenError = () => lastTokenFetchError;

const validateOfficialPayload = (payload: Record<string, unknown>) => {
  const personality = payload.personality as any;
  if (!personality) {
    throw new Error('Payload inválido: falta el objeto personality.');
  }

  const archetype = personality?.core_traits?.archetype;
  if (!SUPPORTED_ARCHETYPES_SET.has(archetype)) {
    throw new Error(`Archetype inválido (${archetype}). Valores permitidos: sage, explorer, rebel, caregiver, everyman.`);
  }

  const fillers = personality?.linguistic_profile?.fillers;
  if (!Array.isArray(fillers) || fillers.length === 0) {
    throw new Error('linguistic_profile.fillers es requerido (al menos un ítem).');
  }

  const punctuation = personality?.linguistic_profile?.punctuation_style;
  if (!punctuation) {
    throw new Error('linguistic_profile.punctuation_style es requerido.');
  }
};

export interface ValidateResponse {
  success: boolean;
  valid: boolean;
  errors: Array<{
    field: string;
    message: string;
  }>;
}

export interface CompileResponse {
  success: boolean;
  compiled_prompt: string;
  estimated_tokens: number;
  provider: string;
}

export interface SimulateResponse {
  success: boolean;
  response: string;
  tokens_used: number;
}

/**
 * Validates a personality against the official API schema
 */
export async function validatePersonality(personality: any): Promise<ValidateResponse> {
  try {
    const response = await executePost('/validate', { personality });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Validation error:', error);
    throw error;
  }
}

/**
 * Compiles a personality into an optimized prompt for LLMs
 */
export async function compilePersonality(
  personality: any,
  provider: string = 'deepseek'
): Promise<CompileResponse> {
  try {
    const response = await executePost('/compile', { personality, provider });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Compilation error:', error);
    throw error;
  }
}

/**
 * Simulates a conversation using a compiled prompt and user input
 */
export async function simulateConversation(
  compiledPrompt: string,
  userInput: string
): Promise<SimulateResponse> {
  try {
    const response = await executePost('/simulate', {
      compiled_prompt: compiledPrompt,
      user_input: userInput
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Simulation error:', error);
    throw error;
  }
}

export interface TestPersonalityResponse {
  success: boolean;
  validation: {
    valid: boolean;
    errors: string[];
    warnings: string[];
  };
  test_response?: string;
  personality_info?: {
    name: string;
    archetype: string;
    temperament: string;
    communication_style: string;
    tone: string[];
    language: string;
  };
  tokens_used?: number;
  compiled_prompt_length?: number;
  test_message?: string;
  timestamp?: string;
  message?: string;
}

export async function testPersonality(personality: any, testMessage: string): Promise<TestPersonalityResponse> {
  try {
    const response = await executePost('/api/v1/personalities/test', {
      personality,
      test_message: testMessage || 'Introduce yourself and tell me about your communication style'
    });

    const data = await response.json();

    if (!response.ok) {
      const tokenError = getLastTokenError();
      const apiMessage = (data && data.message) || tokenError || `HTTP ${response.status}: ${response.statusText}`;
      const validationErrors = Array.isArray(data?.validation?.errors)
        ? data.validation.errors.join(' | ')
        : null;
      const detailedMessage = validationErrors ? `${apiMessage} – ${validationErrors}` : apiMessage;
      const error = new Error(detailedMessage);
      (error as any).validation = data?.validation;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Test personality error:', error);
    throw error;
  }
}

